import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronDown, CheckCircle, Phone, Award, Zap, Shield } from 'lucide-react';
import HeroCanvas from '../components/HeroCanvas';
import HeroOffer from '../components/HeroOffer';
import AnimatedSection from '../components/AnimatedSection';
import { PROMO, usePromo } from '../promo.config';
import { PHONE, PHONE_HREF } from '../contact.config';
import blogPosts from '../data/blogData';
import '../styles/home.css';

// ── Real product images (best quality from each category) ──────────────────
import sheer1    from '../assets/products/sheer_1.jpg';
import bcurtain1 from '../assets/products/bcurtain_1.jpg';
import bblind1   from '../assets/products/bblind_1.jpg';
import pshutter1 from '../assets/products/pshutter_1.jpg';
import oshutter1 from '../assets/products/oshutter_1.jpg';

// ── Background / hero images ────────────────────────────────────────────────
const HERO_BG   = 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1600&q=80';
const ABOUT_IMG = 'https://images.unsplash.com/photo-1616047006789-b7af5afb8c20?w=900&q=80';
const BANNER_IMG= 'https://images.unsplash.com/photo-1618220179428-22790b461013?w=900&q=80';

const products = [
  { num:'01', name:'Sheer Curtains',       img:sheer1,    tag:'Light Filtering',    link:'sheer-curtains-adelaide' },
  { num:'02', name:'Blockout Curtains',    img:bcurtain1, tag:'Complete Blackout',  link:'blackout-curtains-adelaide' },
  { num:'03', name:'Blockout Blinds',      img:bblind1,   tag:'Modern Blinds',      link:'blockout-blinds-adelaide' },
  { num:'04', name:'Plantation Shutters',  img:pshutter1, tag:'Classic Style',      link:'plantation-shutters-adelaide' },
  { num:'05', name:'Outdoor Shutters',     img:oshutter1, tag:'Outdoor Living',     link:'outdoor-shutters-adelaide' },
];

const stats = [
  { num:'500+', label:'Projects Completed' },
  { num:'100%', label:'Custom Made' },
  { num:'5★',   label:'Customer Rating' },
  { num:'Free', label:'In-Home Quote' },
];

const whyUs = [
  { icon:<CheckCircle size={18}/>, title:'Free In-Home Consultation', body:'Our experts visit your space, measure your windows, and provide tailored recommendations — completely free.' },
  { icon:<Award size={18}/>,       title:'Premium Materials',          body:'All products use high-grade materials sourced for durability, elegance, and lasting performance.' },
  { icon:<Zap size={18}/>,         title:'Professional Installation',  body:'Certified installers handle every fitting with precision — clean, efficient, guaranteed perfect.' },
  { icon:<Shield size={18}/>,      title:'Custom Made to Measure',     body:'Every product is manufactured specifically for your windows. No compromises on fit or quality.' },
  { icon:<Phone size={18}/>,       title:'Adelaide-Wide Service',      body:'Proudly serving all Adelaide suburbs with prompt scheduling and reliable on-time delivery.' },
];

const testimonials = [
  { name:'Sam Jones',  suburb:'Norwood SA',      rating:5, text:'"Excellent service from start to finish! From the initial consultation to the final installation, the team was punctual, professional, and extremely knowledgeable. The end result looks absolutely fantastic!"' },
  { name:'Emily R.',   suburb:'Glenelg SA',      rating:5, text:'"My new plantation shutters completely transformed my living room. The quality is outstanding and the installation was quick and clean. I\'ve already recommended YN Dream Curtains to three neighbours."' },
  { name:'David K.',   suburb:'Henley Beach SA', rating:5, text:'"The blockout blinds are perfect — exactly what we needed for the bedroom. The team measured everything precisely and the final product fits like a glove. Very happy from start to finish."' },
];

function StatCounter({ target, label }) {
  const ref = useRef(null);
  const [displayed, setDisplayed] = useState('0');
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    const isNum = !isNaN(parseInt(target));
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        if (!isNum) { setDisplayed(target); return; }
        const end = parseInt(target), suffix = target.replace(/[0-9]/g,'');
        let cur = 0; const step = Math.ceil(end / 60);
        const timer = setInterval(() => { cur = Math.min(cur + step, end); setDisplayed(cur + suffix); if (cur >= end) clearInterval(timer); }, 25);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);
  return (
    <div className="stat-cell" ref={ref}>
      <div className="stat-num">{displayed}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

export default function Home() {
  const [activeT, setActiveT] = useState(0);
  const offerLive = usePromo(60000).phase === 'live';
  useEffect(() => {
    const t = setInterval(() => setActiveT(p => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <Helmet>
        <title>Custom Curtains Adelaide | Blinds & Shutters | YN Dream Curtains</title>
        <meta name="description" content="Adelaide's #1 custom curtains, blinds & plantation shutters specialist. Premium sheer curtains, blockout curtains, roller blinds & shutters. Free in-home consultation & professional installation across all Adelaide suburbs." />
        <link rel="canonical" href="https://yndreamcurtains.com/" />
        <meta property="og:title" content="Custom Curtains Adelaide | Blinds & Shutters | YN Dream Curtains" />
        <meta property="og:description" content="Adelaide's #1 custom curtains, blinds & plantation shutters specialist. Free in-home consultation & professional installation." />
        <meta property="og:url" content="https://yndreamcurtains.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://yndreamcurtains.com/logo512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Custom Curtains Adelaide | Blinds & Shutters | YN Dream Curtains" />
        <meta name="twitter:description" content="Adelaide's #1 custom curtains, blinds & plantation shutters specialist." />
      </Helmet>
      {/* Styles extracted to src/styles/home.css for caching */}

      {/* HERO */}
      <section className="hero">
        <HeroCanvas />
        <div className="hero-img" style={{ backgroundImage:`url(${HERO_BG})` }} />
        <div className="hero-gradient" />
        <div className="hero-content">
          <p className="hero-eyebrow">Adelaide's Premier Curtains & Blinds Specialists</p>
          <h1 className="hero-title">Custom Curtains<br /><em>Adelaide</em></h1>
          <p className="hero-sub">
            Premium custom curtains, blinds & shutters — hand-crafted and professionally installed across Adelaide, South Australia.
          </p>
          <HeroOffer />
          <div className="hero-btns">
            <Link to="/book-online" className="btn-primary">{offerLive ? `Claim ${PROMO.percent}% Off` : 'Get Free Quote'} <ArrowRight size={14}/></Link>
            <Link to="/portfolio"   className="btn-secondary">View Portfolio</Link>
          </div>
        </div>
        <div className="scroll-hint">
          <span>Explore</span>
          <ChevronDown size={16} color="var(--gold)" style={{animation:'float 2s ease-in-out infinite'}}/>
        </div>
      </section>

      {/* STATS */}
      <section className="stats-band">
        <div className="container">
          <div className="stats-row">
            {stats.map(s => <StatCounter key={s.label} target={s.num} label={s.label}/>)}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="products-section">
        <div className="container">
          <AnimatedSection style={{ textAlign:'center' }}>
            <div className="section-label" style={{ justifyContent:'center' }}>Our Collection</div>
            <h2 className="section-title" style={{ marginBottom:14 }}>
              Window Solutions <em>Crafted</em><br/>to Impress
            </h2>
            <p className="section-desc" style={{ maxWidth:520, margin:'0 auto' }}>
              Every product is custom-made to fit your windows perfectly — all photos shown are our real Adelaide installations.
            </p>
          </AnimatedSection>

          <div className="products-grid">
            {products.map((p, i) => (
              <AnimatedSection key={p.num} delay={i * 60}>
                <div className="product-card">
                  <div className="product-img-wrap">
                    <img src={p.img} alt={p.name} loading="lazy"/>
                    <div className="product-img-overlay"/>
                    <span className="product-num-badge">{p.num}</span>
                    <span className="product-tag-badge">{p.tag}</span>
                  </div>
                  <div className="product-body">
                    <div className="product-name">{p.name}</div>
                    <Link to={`/products/${p.link}`} className="product-link">
                      Learn More <ArrowRight size={11}/>
                    </Link>
                  </div>
                  <div className="product-border-bottom"/>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={250} style={{ textAlign:'center', marginTop:44 }}>
            <Link to="/products" className="btn-secondary">View All Products <ArrowRight size={14}/></Link>
          </AnimatedSection>
        </div>
      </section>

      {/* ABOUT / WHY US */}
      <section className="about-section">
        <div className="container">
          <div className="about-grid">
            <AnimatedSection>
              <div className="about-img-wrap">
                <div className="corner-deco tl"/><div className="corner-deco tr"/>
                <img src={ABOUT_IMG} alt="About YN Dream Curtains" loading="lazy"/>
                <div className="about-img-caption">
                  <div className="about-img-cap-title">Adelaide Specialists</div>
                  <div className="about-img-cap-body">Servicing all SA suburbs</div>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={140}>
              <div className="section-label">Why Choose Us</div>
              <h2 className="section-title" style={{ marginBottom:12 }}>
                The YN Dream<br/><em>Difference</em>
              </h2>
              <p className="section-desc" style={{ marginBottom:32 }}>
                We're not just a supplier — we're your dedicated window furnishing partner, committed to quality at every step.
              </p>
              <div className="why-list">
                {whyUs.map((w, i) => (
                  <div className="why-item" key={i}>
                    <div className="why-icon">{w.icon}</div>
                    <div>
                      <div className="why-title">{w.title}</div>
                      <div className="why-body">{w.body}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:32, display:'flex', gap:12, flexWrap:'wrap' }}>
                <Link to="/book-online" className="btn-primary">Book Consultation <ArrowRight size={13}/></Link>
                <Link to="/contact"     className="btn-secondary">Contact Us</Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* BANNER */}
      <section className="feature-banner">
        <div className="feature-banner-img" style={{ backgroundImage:`url(${BANNER_IMG})` }}/>
        <div className="feature-banner-overlay"/>
        <div className="container" style={{ position:'relative', zIndex:2 }}>
          <AnimatedSection style={{ maxWidth:560 }}>
            <div className="section-label">Ready to Transform?</div>
            <h2 className="section-title" style={{ marginBottom:16 }}>
              Elevate Every Room in<br/><em>Your Home</em>
            </h2>
            <p className="section-desc" style={{ marginBottom:36 }}>
              From the living room to the bedroom — our specialists design the perfect window solution for every space.
            </p>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <Link to="/book-online" className="btn-primary">Get Free Quote <ArrowRight size={13}/></Link>
              <Link to="/portfolio"   className="btn-secondary">Browse Portfolio</Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonial-section">
        <div className="container">
          <AnimatedSection style={{ textAlign:'center' }}>
            <div className="section-label" style={{ justifyContent:'center' }}>Customer Reviews</div>
            <h2 className="section-title">What Our <em>Clients</em> Say</h2>
          </AnimatedSection>
          <div className="testimonial-wrap">
            <AnimatedSection delay={100}>
              <div className="testimonial-card">
                <div className="t-quote">"</div>
                <div className="t-stars" aria-label={`${testimonials[activeT].rating} out of 5 stars`} role="img">{'★'.repeat(testimonials[activeT].rating)}</div>
                <p className="t-text">{testimonials[activeT].text}</p>
                <div className="t-author">{testimonials[activeT].name}</div>
                <div className="t-suburb">{testimonials[activeT].suburb}</div>
              </div>
              <div className="t-dots">
                {testimonials.map((_, i) => (
                  <button key={i} className={`t-dot${i === activeT ? ' active' : ''}`} onClick={() => setActiveT(i)} aria-label={`View testimonial ${i + 1} from ${testimonials[i].name}`} style={{background:'none',border:'none',padding:0,cursor:'pointer'}}/>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Section — SEO Content */}
      <section style={{ padding:'100px 0', background:'var(--bg)', borderTop:'1px solid var(--border-soft)' }}>
        <div className="container">
          <AnimatedSection style={{ textAlign:'center' }}>
            <div className="section-label" style={{ justifyContent:'center' }}>Frequently Asked Questions</div>
            <h2 className="section-title">Curtains in <em>Adelaide</em> — Your Questions Answered</h2>
          </AnimatedSection>
          <div style={{ maxWidth:800, margin:'48px auto 0', display:'flex', flexDirection:'column', gap:2 }}>
            {[
              { q:'How much do custom curtains cost in Adelaide?', a:'Custom curtain prices in Adelaide vary depending on fabric, size and style. Every curtain has different price. We offer free in-home consultations with no-obligation quotes tailored to your exact windows.' },
              { q:'Do you offer free in-home curtain consultations in Adelaide?', a:'Yes! YN Dream Curtains provides completely free in-home consultations across all Adelaide suburbs. Our specialists visit your home, take precise measurements, show you fabric samples, and provide a personalised quote — with absolutely no obligation.' },
              { q:'What types of curtains are best for Adelaide\'s climate?', a:'For Adelaide\'s hot summers and cool winters, we recommend a combination of sheer curtains for light filtering and blockout curtains for thermal insulation. Plantation shutters are also excellent for Adelaide homes as they provide year-round climate control and UV protection.' },
              { q:'How long does curtain installation take in Adelaide?', a:'After your in-home consultation, production typically takes 2–4 weeks for custom-made curtains. Installation itself is usually completed within a few hours depending on the number of windows. We service all Adelaide suburbs with prompt scheduling.' },
              { q:'Do you service all Adelaide suburbs?', a:'Absolutely! YN Dream Curtains services all suburbs across Adelaide and greater South Australia, including Norwood, Glenelg, Henley Beach, Burnside, Unley, Prospect, and more. We offer free consultations Adelaide-wide.' },
              { q:'What is the difference between sheer and blockout curtains?', a:'Sheer curtains gently filter natural light while maintaining privacy — perfect for living areas. Blockout curtains completely block light, provide thermal insulation and noise reduction — ideal for bedrooms. Many Adelaide homeowners combine both for maximum versatility.' },
            ].map((faq, i) => (
              <AnimatedSection key={i} delay={i * 50}>
                <div style={{ background:'var(--bg-card)', border:'1px solid var(--border-soft)', padding:'24px 28px', boxShadow:'var(--shadow-sm)' }}>
                  <h3 style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'1rem', fontWeight:600, color:'var(--text)', marginBottom:10, lineHeight:1.4 }}>{faq.q}</h3>
                  <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'0.92rem', fontWeight:300, color:'var(--text-dim)', lineHeight:1.75, margin:0 }}>{faq.a}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "How much do custom curtains cost in Adelaide?", "acceptedAnswer": { "@type": "Answer", "text": "Custom curtain prices in Adelaide vary depending on fabric, size and style. Sheer curtains typically start from $150 per panel, while blockout curtains start from $200 per panel." } },
            { "@type": "Question", "name": "Do you offer free in-home curtain consultations in Adelaide?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! YN Dream Curtains provides completely free in-home consultations across all Adelaide suburbs." } },
            { "@type": "Question", "name": "What types of curtains are best for Adelaide's climate?", "acceptedAnswer": { "@type": "Answer", "text": "For Adelaide's hot summers and cool winters, we recommend sheer curtains for light filtering and blockout curtains for thermal insulation." } },
            { "@type": "Question", "name": "How long does curtain installation take in Adelaide?", "acceptedAnswer": { "@type": "Answer", "text": "Production typically takes 2-4 weeks. Installation is usually completed within a few hours." } },
            { "@type": "Question", "name": "Do you service all Adelaide suburbs?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! We service all suburbs across Adelaide and greater South Australia." } },
            { "@type": "Question", "name": "What is the difference between sheer and blockout curtains?", "acceptedAnswer": { "@type": "Answer", "text": "Sheer curtains filter light while maintaining privacy. Blockout curtains completely block light and provide thermal insulation." } }
          ]
        }) }} />
      </section>

      {/* Latest from Our Blog */}
      <section style={{ padding:'100px 0', background:'var(--bg)', borderTop:'1px solid var(--border-soft)' }}>
        <div className="container">
          <AnimatedSection style={{ textAlign:'center' }}>
            <div className="section-label" style={{ justifyContent:'center' }}>Expert Advice</div>
            <h2 className="section-title">Latest from Our <em>Blog</em></h2>
            <p className="section-desc" style={{maxWidth:600,margin:'0 auto 40px'}}>Expert curtain, blind and shutter guides from Adelaide's trusted window furnishing specialists.</p>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }}>
              {blogPosts.slice(0, 3).map(post => (
                <Link to={`/blog/${post.slug}`} key={post.slug} style={{
                  background:'var(--bg-card)', border:'1px solid var(--border-soft)', overflow:'hidden',
                  textDecoration:'none', color:'inherit', transition:'all 0.4s', boxShadow:'var(--shadow-sm)',
                  display:'flex', flexDirection:'column'
                }}>
                  <img src={post.image} alt={post.title} style={{ width:'100%', height:160, objectFit:'cover', display:'block' }} loading="lazy" width="400" height="160" />
                  <div style={{ padding:'16px 16px 18px', flex:1, display:'flex', flexDirection:'column' }}>
                    <div style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'0.72rem', color:'var(--text-light)', marginBottom:8 }}>{post.date} · {post.readTime}</div>
                    <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', fontWeight:600, color:'var(--text)', lineHeight:1.25, marginBottom:8 }}>{post.title}</h3>
                    <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:'0.82rem', fontWeight:300, color:'var(--text-dim)', lineHeight:1.55, flex:1 }}>{post.excerpt}</p>
                    <span style={{ fontFamily:"'Outfit',sans-serif", fontSize:'0.54rem', fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'var(--gold)', display:'inline-flex', alignItems:'center', gap:5, marginTop:10 }}>Read More <ArrowRight size={12} /></span>
                  </div>
                </Link>
              ))}
            </div>
            <div style={{ textAlign:'center', marginTop:32 }}>
              <Link to="/blog" className="btn-secondary" style={{display:'inline-flex',alignItems:'center',gap:8}}>View All Articles <ArrowRight size={13}/></Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <AnimatedSection>
            <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:'0.62rem', letterSpacing:'0.35em', color:'rgba(255,255,255,0.65)', textTransform:'uppercase', marginBottom:16, fontWeight:500 }}>
              Get Started Today
            </div>
            <h2 className="cta-title">Book Your Free<br/>In-Home Consultation</h2>
            <p className="cta-sub">
              Our Adelaide specialists visit your home, measure your windows, and present a personalised quote — at no cost.
            </p>
            <div style={{ display:'flex', gap:16, justifyContent:'center', flexWrap:'wrap' }}>
              <Link to="/book-online" className="btn-cta-white">Book Consultation <ArrowRight size={14}/></Link>
              <Link to="/portfolio"   className="btn-cta-outline">Browse Portfolio</Link>
            </div>
            <p className="cta-call">Prefer to talk? Call <a href={PHONE_HREF}>{PHONE}</a></p>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
}
